/*
 * Copyright 2026 Sony Corporation
 */

import type { RealtimeEvent } from '@ips-device-verse/twin-service/schema/realtime';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { resolveLook } from '../creative-look/resolve';
import type { ResolvedOptics } from '../scene/camera-optics';
import type { SceneEditor } from '../scene/scene-editor';
import { renderStill } from '../scene/still';
import type { Transform } from '../scene/types';
import { ApiError } from '../services/api-client';

export interface CaptureSubject {
  pose: Transform;
  optics: ResolvedOptics;
}

export interface UseCaptureResponderOptions {
  worldId: string;
  subscribe: (listener: (event: RealtimeEvent) => void) => () => void;
  /** Where a device stands and what it sees through, right now. */
  subjectOf: (deviceId: string) => CaptureSubject | undefined;
}

/**
 * Older than this and the gateway has already given up and used its test
 * pattern; rendering it would only upload a picture nobody takes.
 */
const STALE_MS = 15_000;

/**
 * Renders a still whenever a virtual camera's shutter is released.
 *
 * The gateway reports a capture request in the device's shadow; this tab, which
 * has the world loaded, renders that camera's view and posts it. Every open tab
 * of the world does the same and the service keeps the first, so a request is
 * answered as long as anyone is looking.
 */
export const useCaptureResponder = ({ worldId, subscribe, subjectOf }: UseCaptureResponderOptions) => {
  const { api } = useAuth();
  const [editor, setEditor] = useState<SceneEditor>();
  const answered = useRef(new Set<string>());

  // Read when a request arrives, so the still is of where the camera is then.
  const subjectRef = useRef(subjectOf);
  useEffect(() => {
    subjectRef.current = subjectOf;
  });

  useEffect(() => {
    if (!editor) {
      return;
    }
    return subscribe((event) => {
      if (event.type !== 'device_state' || event.world_id !== worldId) {
        return;
      }
      const request = event.state.reported?.captureRequest;
      if (!request || answered.current.has(request.id)) {
        return;
      }
      answered.current.add(request.id);
      if (Math.abs(Date.now() - request.at) > STALE_MS) {
        return;
      }
      const subject = subjectRef.current(event.device_id);
      if (!subject) {
        return;
      }
      const look = resolveLook({
        state: { device_id: event.device_id, thing_name: '', state: event.state },
        pending: {},
        rejected: {},
        baseline: {},
        timedOut: {},
      }).values;

      // A 409 means another tab got there first, which is the normal race.
      renderStill(editor, subject.pose, subject.optics, look)
        .then((image) => api.createCapture(worldId, event.device_id, { request_id: request.id, image }))
        .catch((error: unknown) => {
          if (!(error instanceof ApiError && error.status === 409)) {
            console.warn('capture not delivered', error);
          }
        });
    });
  }, [editor, subscribe, worldId, api]);

  return { attach: useCallback((context: { editor: SceneEditor }) => setEditor(context.editor), []) };
};
